import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useState } from 'react';
import { ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';

import { ApprovalPhotos, ApprovalPhotoViewer, type OpenPhoto } from './approval-photos';

import { Markdown } from '@/components/common/markdown';
import { Button, Modal, Text } from '@/components/ui';
import type { TodoItem } from '@/hooks/use-todo-items';
import { formatMoneyCents } from '@/utils/format-money';

/** Widest the popup gets. Fixed rather than content-sized, so a long description cannot stretch it. */
const MAX_POPUP_WIDTH = 640;
/** Horizontal padding inside the popup, on each side. */
const POPUP_PADDING = 20;

/**
 * The Approve confirmation for a To Do row.
 *
 * A structured dialog rather than an `Alert` string: the property manager is signing off
 * money, and the four things they checked on the paper sheet — where, what was asked, what
 * the customer will read, and what it costs — deserve headings, a callout, and markdown
 * rendering rather than a wall of text in one font. Descriptions are markdown fields, so
 * they go through the same `Markdown` renderer the work-order page uses.
 *
 * Titled REVIEW, like the row button that opens it: opening it approves nothing, and the
 * before/after photos are part of what is being reviewed.
 *
 * ── LAYOUT ──
 *
 * Header, a body that SCROLLS, and a footer PINNED below it. However long the description and
 * however many photos, Cancel and Approve stay on screen: the popup is capped at the Modal's
 * 90% height and only the body gives up space. The width is fixed (640, or 90% of a narrower
 * screen) — sized by its content it stretched to the whole window for one long description.
 *
 * Tapping a photo opens it FULL SCREEN over this popup; closing it lands back on the review
 * exactly where the PM left it.
 */
export const ApproveConfirmModal: React.FC<{
	item: TodoItem | null;
	busy: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}> = ({ item, busy, onCancel, onConfirm }) => {
	const { width: windowWidth } = useWindowDimensions();
	const [viewing, setViewing] = useState<OpenPhoto | null>(null);
	const closeViewer = useCallback(() => setViewing(null), []);
	if (!item) return null;
	const woLabel = item.title.replace(/^Approve /, '');
	const where = [item.associationName, item.subtitle].filter(Boolean);
	const popupWidth = Math.min(MAX_POPUP_WIDTH, windowWidth * 0.9);
	// The viewer is per-opening: reopening the popup starts on the review, not the last photo.
	const close = () => {
		setViewing(null);
		onCancel();
	};
	const confirm = () => {
		setViewing(null);
		onConfirm();
	};

	return (
		<Modal
			visible
			onDismiss={close}
			onSubmit={busy ? undefined : confirm}
			dismissOnBackdrop
			contentContainerStyle={[styles.container, { width: popupWidth }]}
		>
			<View style={styles.header} testID="approve-confirm-header">
				<View style={styles.iconWrap}>
					<Ionicons name="construct-outline" size={20} color="#ef6c00" />
				</View>
				<View style={{ flex: 1, minWidth: 0 }}>
					<Text style={styles.title} testID="approve-confirm-title">Review {woLabel}</Text>
					{where.length > 0 && (
						<Text style={styles.where} numberOfLines={2}>
							{where.map((w, i) => (
								<React.Fragment key={String(w)}>
									{i > 0 && <Text style={styles.dim}>{'  ·  '}</Text>}
									<Text style={i === 0 ? styles.association : undefined}>{w}</Text>
								</React.Fragment>
							))}
						</Text>
					)}
				</View>
			</View>

			<ScrollView
				style={styles.body}
				contentContainerStyle={styles.bodyContent}
				testID="approve-confirm-body"
			>
				<View style={styles.amountRow}>
					<Text style={styles.amountLabel}>Amount to bill</Text>
					<Text style={styles.amount} testID="approve-confirm-amount">
						{item.costCents != null ? formatMoneyCents(item.costCents) : 'Not yet known'}
					</Text>
				</View>
				<Section title="Work requested" empty="No request text recorded.">
					{item.workRequested}
				</Section>
				{item.workOrderId ? (
					<ApprovalPhotos
						workOrderId={item.workOrderId}
						width={popupWidth - POPUP_PADDING * 2}
						onOpen={setViewing}
					/>
				) : null}
				<Section title="Customer will see" empty="No customer description yet — the invoice memo will be blank.">
					{item.customerDescription}
				</Section>
			</ScrollView>

			<View style={styles.footer} testID="approve-confirm-footer">
				<Text style={styles.footnote}>
					Approving marks the work order approved and sends it to accounting to be billed.
				</Text>
				<View style={styles.actions}>
					<Button onPress={close} disabled={busy} testID="approve-confirm-cancel"><Text>Cancel</Text></Button>
					<Button
						mode="contained"
						variant="success"
						onPress={confirm}
						disabled={busy}
						testID="approve-confirm-approve"
					>
						{/* A string, not <Text>: a nested Text forces its own dark colour over the label's. */}
						{busy ? 'Approving…' : 'Approve'}
					</Button>
				</View>
			</View>

			{/* Full screen, over this popup — which stays mounted underneath, scroll position and all. */}
			<ApprovalPhotoViewer open={viewing} onChange={setViewing} onClose={closeViewer} />
		</Modal>
	);
};

const Section: React.FC<{ title: string; empty: string; children?: string }> = ({ title, empty, children }) => (
	<View style={styles.section}>
		<Text style={styles.sectionTitle}>{title}</Text>
		{children
			? <View style={styles.sectionBody}><Markdown>{children}</Markdown></View>
			: <Text style={styles.sectionEmpty}>{empty}</Text>}
	</View>
);

const styles = StyleSheet.create({
	// Padding moves onto the three bands so the footer's rule can run edge to edge.
	container: { padding: 0, overflow: 'hidden' },
	header: {
		flexDirection: 'row', alignItems: 'flex-start', gap: 12,
		paddingHorizontal: POPUP_PADDING, paddingTop: POPUP_PADDING, paddingBottom: 12,
	},
	iconWrap: {
		width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
		backgroundColor: '#fff3e0',
	},
	title: { fontSize: 18, fontWeight: '700', color: '#263238' },
	where: { fontSize: 13, color: '#37474f', marginTop: 2 },
	association: { fontWeight: '600' },
	dim: { color: '#90a4ae' },
	/**
	 * The only band that gives up height. `flexShrink: 1` + `minHeight: 0` let it shrink below
	 * its content inside the Modal's `maxHeight: 90%` — and then scroll — instead of pushing
	 * the footer off the bottom of the screen.
	 */
	body: { flexGrow: 0, flexShrink: 1, minHeight: 0 },
	bodyContent: { paddingHorizontal: POPUP_PADDING, paddingBottom: 8 },
	amountRow: {
		flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
		paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8,
		backgroundColor: '#e8f5e9', marginBottom: 12,
	},
	amountLabel: { fontSize: 13, fontWeight: '600', color: '#2e7d32' },
	amount: { fontSize: 18, fontWeight: '700', color: '#2e7d32', fontVariant: ['tabular-nums'] },
	section: { marginBottom: 12 },
	sectionTitle: {
		fontSize: 11, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase',
		color: '#78909c', marginBottom: 4,
	},
	sectionBody: {
		paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8,
		backgroundColor: '#f5f7f8', borderLeftWidth: 3, borderLeftColor: '#cfd8dc',
	},
	sectionEmpty: { fontSize: 13, fontStyle: 'italic', color: '#90a4ae' },
	footer: {
		flexShrink: 0,
		paddingHorizontal: POPUP_PADDING, paddingTop: 12, paddingBottom: POPUP_PADDING,
		borderTopWidth: 1, borderTopColor: '#eceff1',
	},
	footnote: { fontSize: 12, color: '#78909c' },
	actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 12 },
});
